import type { FieldNode } from '@captomi/schema-form';

// Painter-only profile fields that live at the TOP LEVEL of the office schema (not inside the
// discriminated `profile.experience` union), so the schema-driven renderer would otherwise show
// them for every vertical. SaaS tenants have no service areas / specialties / crew, so these are
// meaningless to them. We hide them at render time for SAAS — a display-only fix; the fields still
// exist in the schema + data (empty for SaaS). Relocating them into the painter `experience`
// variant is the deeper data-model change (needs a migration of live painter docs) — deferred.
export const PAINTER_ONLY_PROFILE_PATHS: ReadonlySet<string> = new Set([
  'profile.service_areas',
  'profile.specialties',
  'profile.strengths',
  'profile.team_size',
]);

// Trade-INDEPENDENT profile fields: facts about a BUSINESS, not about a trade. Every vertical has an
// address and a founding year, so these render for every trade and can never be hidden from one.
// CSLB is only the SOURCE that populates the address for painters — not a reason the field is
// painter-ONLY; filing it that way leaves a SaaS tenant with no address field at all (there is no other).
//
// `hidden_for_trade` SUBTRACTS this set from whatever is being pruned, so a path listed here stays
// visible even if a future schema bump also files it as painter-only. See docs/admin.md §6.5.
export const TRADE_INDEPENDENT_PROFILE_PATHS: ReadonlySet<string> = new Set([
  'profile.business_address',
  'profile.years_in_business',
]);

/** Returns `schema` with the given paths pruned from every `fields`/`variants` list (deep, pure). */
function prune(node: FieldNode, hidden: ReadonlySet<string>): FieldNode {
  return {
    ...node,
    ...(node.fields && {
      fields: node.fields.filter((f) => !hidden.has(f.path)).map((f) => prune(f, hidden)),
    }),
    ...(node.variants && { variants: node.variants.map((v) => prune(v, hidden)) }),
  };
}

/**
 * The paths actually hidden for a tenant of `trade` — painter-only MINUS anything trade-independent.
 * The subtraction is the invariant: a trade-independent field (a business address, a founding year) is
 * shown to EVERY trade, and cannot be hidden by mis-filing it as painter-only.
 *
 * `painter_only` is injectable so the guard itself is testable: pass a set that wrongly contains
 * `profile.business_address` and the result still won't hide it.
 */
export function hidden_for_trade(
  trade: unknown,
  painter_only: ReadonlySet<string> = PAINTER_ONLY_PROFILE_PATHS,
): ReadonlySet<string> {
  if (trade !== 'SAAS') return new Set(); // PAINTER, and any absent/unknown/future trade, see everything
  return new Set([...painter_only].filter((p) => !TRADE_INDEPENDENT_PROFILE_PATHS.has(p)));
}

/**
 * The schema to render for a tenant of `trade`. For SAAS, painter-only top-level profile fields are
 * hidden; every other trade (incl. PAINTER and absent/unknown) gets the schema unchanged. Fields in
 * `TRADE_INDEPENDENT_PROFILE_PATHS` survive for every trade, always.
 */
export function schema_for_trade(schema: FieldNode, trade: unknown): FieldNode {
  const hidden = hidden_for_trade(trade);
  return hidden.size ? prune(schema, hidden) : schema;
}
